import { apiRequest } from './core/client';

// ============================================================================
// Overview Types
// ============================================================================

export interface OverviewFetchOptions {
  limit?: number;
  folderId?: string | null;
  includeSurveys?: boolean;
  surveySince?: string;
  unreadOnly?: boolean;
  inboxOnly?: boolean;
  minScore?: number;
  signal?: AbortSignal;
}

export interface OverviewItem {
  id: string;
  accountId: string;
  subject: string;
  snippet: string;
  from: { name?: string; email: string } | null;
  date: string;
  priority_score: number;
  summary: string | null;
  category: 'deadline' | 'forwarded' | 'financial' | 'request' | 'general';
  suggested_actions: string[];
  signals: Record<string, unknown>;
  status: 'pending' | 'surfaced' | 'dismissed' | 'actioned';
  seen: boolean;
}

export interface SurveyAggregation {
  total_responses: number;
  avg_rating: number | null;
  rating_distribution: {
    five_star: number;
    four_star: number;
    three_star: number;
    low_star: number;
  };
  detailed_averages: {
    price: number | null;
    product: number | null;
    delivery: number | null;
    packaging: number | null;
    count: number;
  } | null;
  flagged_count: number;
  flagged_surveys: Array<{
    message_id: string;
    account_id: string;
    subject: string | null;
    respondent_name: string | null;
    rating: string | null;
    comment: string | null;
    flag_reason: string | null;
    date: string | null;
    location: string | null;
    received_at: string | null;
  }>;
  all_surveys: Array<{
    message_id: string;
    account_id: string;
    subject: string | null;
    respondent_name: string | null;
    rating: string | null;
    comment: string | null;
    flag_reason: string | null;
    date: string | null;
    location: string | null;
    received_at: string | null;
  }>;
}

export interface OverviewResponse {
  items: OverviewItem[];
  surveys: SurveyAggregation | null;
  generated_at: string;
}

// ============================================================================
// Overview Operations
// ============================================================================

export async function fetchOverview(
  accountId: string,
  options: OverviewFetchOptions = {}
): Promise<OverviewResponse> {
  const {
    limit = 5,
    folderId = null,
    includeSurveys = true,
    surveySince = '7 days',
    unreadOnly = false,
    inboxOnly = true,
    minScore = 0,
    signal,
  } = options;

  const query: Record<string, string | number | boolean | null | undefined> = {
    limit,
    includeSurveys,
    surveySince,
    unreadOnly,
    inboxOnly,
    minScore,
  };
  if (folderId) {
    query.folderId = folderId;
  }

  const response = await apiRequest<unknown>({
    path: `/accounts/${encodeURIComponent(accountId)}/overview`,
    method: 'GET',
    query,
    signal,
  });

  const record = response as Record<string, unknown>;
  return {
    items: Array.isArray(record.items) ? (record.items as OverviewItem[]) : [],
    surveys: record.surveys as SurveyAggregation | null,
    generated_at:
      typeof record.generated_at === 'string' ? record.generated_at : new Date().toISOString(),
  };
}

export async function updateOverviewStatus(
  accountId: string,
  messageId: string,
  status: 'dismissed' | 'actioned' | 'surfaced' | 'pending'
): Promise<{ success: boolean; status: string; dismissed_at: string | null }> {
  const response = await apiRequest<unknown>({
    path: `/messages/${encodeURIComponent(messageId)}/overview-status`,
    method: 'POST',
    query: { accountId },
    body: { status },
  });
  const record = response as Record<string, unknown>;
  return {
    success: Boolean(record.success),
    status: typeof record.status === 'string' ? record.status : status,
    dismissed_at: typeof record.dismissed_at === 'string' ? record.dismissed_at : null,
  };
}

export async function dismissAllOverviewItems(
  accountId: string,
  messageIds: string[]
): Promise<{ success: boolean; dismissed: number; total: number }> {
  const response = await apiRequest<unknown>({
    path: `/accounts/${encodeURIComponent(accountId)}/overview/dismiss-all`,
    method: 'POST',
    body: { messageIds },
  });
  const record = response as Record<string, unknown>;
  return {
    success: Boolean(record.success),
    dismissed: typeof record.dismissed === 'number' ? record.dismissed : 0,
    total: typeof record.total === 'number' ? record.total : messageIds.length,
  };
}
